import { useState, useEffect, useCallback } from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { userService } from '../services/userService';
import UserTable from '../components/users/UserTable';
import CreateUserModal from '../components/users/CreateUserModal';
import EditUserModal from '../components/users/EditUserModal';
import ChangePasswordModal from '../components/users/ChangePasswordModal';
import DeleteUserModal from '../components/users/DeleteUserModal';
import '../styles/UserManagementPage.scss';

const UserManagementPage = () => {
  const { isUserManager } = useAuth();
  
  const { isInitialized, showLoading, hideLoading, showSuccess } = useUI();

  // Data state
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadUsers = useCallback(async () => {
    showLoading('Loading user management...');
    setLoadError('');
    try {
      const response = await userService.getAllUsers();
      if (response.success) {
        setData({
          users: response.users || [],
          userTypes: response.userTypes || [],
        });
      } else {
        setLoadError(response.error || 'Failed to load users.');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setLoadError('An error occurred while loading users.');
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  // Load users on mount
  useEffect(() => {
    if (isInitialized && isUserManager) {
      loadUsers();
    }
  }, [isInitialized, isUserManager, loadUsers]);

  if (!data && !loadError) {
    // Display nothing when data is not ready
    return <></>;
  }

  // Create user handlers
  const handleCreateClick = () => {
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async (userData) => {
    try {
      const response = await userService.createUser(userData);
      if (response.success) {
        setShowCreateModal(false);
        await loadUsers();
        showSuccess('User created successfully.');
        return { success: true };
      } else {
        return {
          success: false,
          error: response.error,
          validationErrors: response.validationErrors,
        };
      }
    } catch (err) {
      console.error('Error creating user:', err);
      return { success: false, error: 'An error occurred while creating the user.' };
    }
  };

  // Edit user handlers
  const handleEditClick = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (userData) => {
    try {
      const response = await userService.updateUser(selectedUser.id, userData);
      if (response.success) {
        setShowEditModal(false);
        setSelectedUser(null);
        await loadUsers();
        showSuccess('User updated successfully.');
        return { success: true };
      } else {
        return {
          success: false,
          error: response.error,
          validationErrors: response.validationErrors,
        };
      }
    } catch (err) {
      console.error('Error updating user:', err);
      return { success: false, error: 'An error occurred while updating the user.' };
    }
  };

  // Change password handlers
  const handleChangePasswordClick = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (newPassword) => {
    try {
      const response = await userService.changePassword(selectedUser.id, newPassword);
      if (response.success) {
        setShowPasswordModal(false);
        setSelectedUser(null);
        showSuccess('Password changed successfully.');
        return { success: true };
      } else {
        return {
          success: false,
          error: response.error,
          validationErrors: response.validationErrors,
        };
      }
    } catch (err) {
      console.error('Error changing password:', err);
      return { success: false, error: 'An error occurred while changing the password.' };
    }
  };

  // Delete user handlers
  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await userService.deleteUser(selectedUser.id);
      if (response.success) {
        setShowDeleteModal(false);
        setSelectedUser(null);
        await loadUsers();
        showSuccess('User deleted successfully.');
        return { success: true };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      return { success: false, error: 'An error occurred while deleting the user.' };
    }
  };

  // Close modal handlers
  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowPasswordModal(false);
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  return (
    <Container className="user-management-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>User Management</h2>
          <div>
            <Button variant="primary" onClick={handleCreateClick}>
              Create New User
            </Button>
          </div>
        </div>

        {loadError && (
          <Alert variant="danger">{loadError}</Alert>
        )}

        <UserTable
          users={data.users}
          userTypes={data.userTypes}
          onEdit={handleEditClick}
          onChangePassword={handleChangePasswordClick}
          onDelete={handleDeleteClick}
        />

      {/* Modals */}
      <CreateUserModal
        show={showCreateModal}
        userTypes={data.userTypes}
        onSubmit={handleCreateSubmit}
        onClose={handleCloseModals}
      />

      <EditUserModal
        show={showEditModal}
        user={selectedUser}
        userTypes={data.userTypes}
        onSubmit={handleEditSubmit}
        onClose={handleCloseModals}
      />

      <ChangePasswordModal
        show={showPasswordModal}
        user={selectedUser}
        onSubmit={handlePasswordSubmit}
        onClose={handleCloseModals}
      />

      <DeleteUserModal
        show={showDeleteModal}
        user={selectedUser}
        onConfirm={handleDeleteConfirm}
        onClose={handleCloseModals}
      />
    </Container>
  );
};

export default UserManagementPage;
